import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCoachCacheShareSession, saveCoachCacheShareSession } from "../../../auth/Onboarding/services/onboardingServices";
import { sessionSliceFor } from "../allocationModel";

const SAVE_DELAY_MS = 600;
const RETRY_DELAY_MS = 5000;
const MAX_RETRIES = 3;

// Saves team slices in the background. Each slice is the team's whole state,
// so the newest one always supersedes what was not saved yet. A team counts
// as saved only once the server accepts its slice; one request per team is
// in flight at a time. A slice the server refuses (4xx) is not resent (the
// next edit sends a fresh one); network and server errors retry a few times.
const createSaver = () => {
  const latest = {}; // teamKey -> newest slice, saved or not
  const held = {}; // teamKey -> JSON of the slice the server holds or refused
  const failures = {};
  const busy = new Set();
  let lastKey = "";
  let timer = null;
  let stopped = false;

  const dirty = (key) => key in latest && JSON.stringify(latest[key]) !== held[key];

  const later = (ms) => {
    clearTimeout(timer);
    if (!stopped) timer = setTimeout(flush, ms);
  };

  const send = (key) => {
    const json = JSON.stringify(latest[key]);
    busy.add(key);
    saveCoachCacheShareSession({ teamKey: key, ...latest[key] })
      .then(() => {
        held[key] = json;
        failures[key] = 0;
      })
      .catch((err) => {
        const code = err?.response?.status;
        if (code >= 400 && code < 500) held[key] = json;
        else failures[key] = (failures[key] || 0) + 1;
      })
      .finally(() => {
        busy.delete(key);
        if (!dirty(key)) return;
        if (JSON.stringify(latest[key]) !== json) later(SAVE_DELAY_MS);
        else if (failures[key] <= MAX_RETRIES) later(RETRY_DELAY_MS);
      });
  };

  const flush = () => {
    clearTimeout(timer);
    Object.keys(latest)
      .filter((key) => !busy.has(key) && dirty(key))
      .forEach(send);
  };

  return {
    latest,
    load: (teams) =>
      Object.entries(teams || {}).forEach(([key, slice]) => {
        latest[key] = slice;
        held[key] = JSON.stringify(slice);
      }),
    // A team switch sends the previous team's edits at once.
    update: (key, slice) => {
      const switched = lastKey && lastKey !== key;
      lastKey = key;
      latest[key] = slice;
      failures[key] = 0;
      if (switched) flush();
      else if (dirty(key)) later(SAVE_DELAY_MS);
    },
    flush,
    start: () => {
      stopped = false;
    },
    stop: () => {
      stopped = true;
      clearTimeout(timer);
    },
  };
};

// The coach's saved CacheShare™ work, one slice per team. Loads once, hands
// out a team's slice (the newest local one, saved or not), and saves the
// open team's slice shortly after it changes. Nothing is saved when the
// session could not be loaded (an empty slice must not overwrite real work).
export const useCoachCacheShareSession = ({ teamKey, slice }) => {
  const legacy = useRef({});
  const [saver] = useState(createSaver);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    fetchCoachCacheShareSession()
      .then((data) => {
        if (cancelled) return;
        const { teams, ...rest } = data || {};
        legacy.current = rest;
        saver.load(teams);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [saver]);

  useEffect(() => {
    if (status === "ready" && teamKey) saver.update(teamKey, slice);
  }, [status, teamKey, slice, saver]);

  // Leaving the page sends what is still unsaved.
  useEffect(() => {
    saver.start();
    return () => {
      saver.flush();
      saver.stop();
    };
  }, [saver]);

  const sliceFor = useCallback((record) => sessionSliceFor({ ...legacy.current, teams: saver.latest }, record), [saver]);

  return { status, sliceFor };
};
